import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import ScheduleIcon from '@mui/icons-material/Schedule';
import api from '../../services/api';

/**
 * Visão geral dos domínios bloqueados:
 *   - Total efetivamente bloqueando (categorias ativas + não expiradas)
 *   - Breakdown: permanentes vs temporários (com data de expiração)
 *   - Off-screen: em categorias inativas / expiradas
 */
export default function BlockedDomainsCard() {
    const [state, setState] = useState({ loading: true });

    useEffect(() => {
        api.get('/dashboard/oceanide/domains_summary')
            .then((res) => setState({ ...res.data, loading: false }))
            .catch(() => setState({ loading: false }));
    }, []);

    if (state.loading) {
        return (
            <Paper elevation={2} sx={{ m: 2, p: 2, minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} />
            </Paper>
        );
    }

    const total = state.total || 0;
    const totalCategories = state.categories_total || 0;

    return (
        <Paper
            elevation={2}
            sx={{
                m: 2,
                p: 2,
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '4px solid #e01b29',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BlockIcon color="primary" />
                <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Domínios Bloqueados
                </Typography>
            </Box>

            <Typography variant="h2" sx={{ fontFamily: 'Montserrat', fontWeight: 400, color: '#e01b29', mb: 1, lineHeight: 1.1 }}>
                {total.toLocaleString('pt-BR')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2 }}>
                em {state.categories_active || 0} categoria(s) ativa(s) de {totalCategories}
            </Typography>

            <Stack direction="column" spacing={0.75}>
                <Tooltip title="Bloqueio sem prazo definido — categorias sem campo de expiração">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>Permanentes</Typography>
                        <Chip size="small" label={(state.permanent || 0).toLocaleString('pt-BR')} color="success" variant="outlined" />
                    </Box>
                </Tooltip>
                <Tooltip title="Bloqueio com data de expiração futura — categoria com campo expired preenchido">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon fontSize="small" sx={{ color: '#ed6c02' }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>Temporários</Typography>
                        <Chip size="small" label={(state.temporary || 0).toLocaleString('pt-BR')} color="warning" variant="outlined" />
                    </Box>
                </Tooltip>
                {(state.scheduled || 0) > 0 && (
                    <Tooltip title={`Vão entrar em vigor automaticamente — ${state.categories_scheduled || 0} categoria(s) agendada(s) com data futura`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon fontSize="small" sx={{ color: '#0288d1' }} />
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>Agendados</Typography>
                            <Chip size="small" label={(state.scheduled || 0).toLocaleString('pt-BR')} color="info" variant="outlined" />
                        </Box>
                    </Tooltip>
                )}
                {(state.in_inactive_categories || 0) > 0 && (
                    <Tooltip title="Domínios em categorias desativadas — não estão bloqueando agora">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.disabled' }}>Em categorias inativas</Typography>
                            <Chip size="small" label={(state.in_inactive_categories || 0).toLocaleString('pt-BR')} variant="outlined" />
                        </Box>
                    </Tooltip>
                )}
                {(state.in_expired_categories || 0) > 0 && (
                    <Tooltip title="Domínios em categorias com expiração já passada">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.disabled' }}>Em categorias expiradas</Typography>
                            <Chip size="small" label={(state.in_expired_categories || 0).toLocaleString('pt-BR')} variant="outlined" />
                        </Box>
                    </Tooltip>
                )}
            </Stack>
        </Paper>
    );
}
